import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  AttachMoney,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  PieChart as PieChartIcon,
  ShowChart as ChartIcon,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend } from 'recharts';
import { portfolioService, Portfolio, Holding } from '../services/portfolioService';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const COLORS = ['#2196F3', '#4CAF50', '#FF9800', '#E91E63', '#9C27B0', '#00BCD4', '#795548', '#607D8B'];

interface HoldingWithPrices extends Holding {
  current_price: number;
  current_value: number;
  profit_loss: number;
  profit_loss_percentage: number;
}

interface PortfolioData {
  portfolio: Portfolio;
  holdings: HoldingWithPrices[];
  totalHoldings: number;
  totalInvested: number;
  currentValue: number;
  profitLoss: number;
  profitLossPercentage: number;
}

interface AggregatedHolding {
  symbol: string;
  name: string;
  totalQuantity: number;
  totalInvested: number;
  currentValue: number;
  profitLoss: number;
  profitLossPercentage: number;
  portfolioCount: number;
}

const Dashboard: React.FC = () => {
  const [portfoliosData, setPortfoliosData] = useState<PortfolioData[]>([]);
  const [aggregatedHoldings, setAggregatedHoldings] = useState<AggregatedHolding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Modal states for detailed views
  const [allocationModalOpen, setAllocationModalOpen] = useState(false);
  const [holdingsModalOpen, setHoldingsModalOpen] = useState(false);
  
  // Zoom levels for charts in modals
  const [pieChartZoom, setPieChartZoom] = useState(15); // Number of stocks to show in pie chart
  const [barChartZoom, setBarChartZoom] = useState(50); // Number of holdings to show in bar chart
  
  // Selected portfolio for detailed stock allocation view
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<number | null>(null);
  
  // Display mode for charts: absolute values or percentage
  const [showPercentage, setShowPercentage] = useState(false);
  
  // Get holdings for selected portfolio or all aggregated holdings
  const getDisplayHoldings = useCallback(() => {
    if (selectedPortfolioId === null) {
      return aggregatedHoldings;
    }
    const selectedPortfolio = portfoliosData.find(pd => pd.portfolio.id === selectedPortfolioId);
    if (!selectedPortfolio) {
      return aggregatedHoldings;
    }
    // Return holdings from selected portfolio, sorted by invested amount
    return selectedPortfolio.holdings
      .map(h => ({
        symbol: h.symbol,
        name: h.name || h.symbol,
        totalQuantity: h.quantity,
        totalInvested: h.quantity * h.average_price,
        currentValue: h.current_value,
        profitLoss: h.profit_loss,
        profitLossPercentage: h.profit_loss_percentage,
        portfolioCount: 1,
      }))
      .sort((a, b) => b.totalInvested - a.totalInvested);
  }, [selectedPortfolioId, portfoliosData, aggregatedHoldings]);
  
  
  // Handle portfolio pie segment click
  const handlePortfolioPieClick = useCallback((data: any) => {
    if (data && data.payload) {
      const clickedName = data.payload.name;
      const portfolio = portfoliosData.find(pd => pd.portfolio.name === clickedName);
      if (portfolio) {
        setSelectedPortfolioId(portfolio.portfolio.id);
        setPieChartZoom(15); // Reset zoom when switching portfolios
      }
    }
  }, [portfoliosData]);
  
  // Refs for chart containers to attach non-passive wheel listeners
  const pieChartRef = useRef<HTMLDivElement>(null);
  const barChartRef = useRef<HTMLDivElement>(null);
  const allocationBarChartRef = useRef<HTMLDivElement>(null);
  
  // Effect to attach non-passive wheel listeners (React's onWheel is passive by default)
  useEffect(() => {
    const pieChartEl = pieChartRef.current;
    const barChartEl = barChartRef.current;
    const allocationBarEl = allocationBarChartRef.current;
    
    const handlePieWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const displayHoldings = getDisplayHoldings();
      setPieChartZoom(prev => {
        const delta = e.deltaY > 0 ? -2 : 2;
        const newValue = prev + delta;
        return Math.max(5, Math.min(displayHoldings.length, newValue));
      });
    };
    
    const handleBarWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const displayHoldings = getDisplayHoldings();
      setBarChartZoom(prev => {
        const delta = e.deltaY > 0 ? -5 : 5;
        const newValue = prev + delta;
        return Math.max(10, Math.min(displayHoldings.length, newValue));
      });
    };
    
    // Attach with { passive: false } to allow preventDefault
    if (pieChartEl) {
      pieChartEl.addEventListener('wheel', handlePieWheel, { passive: false });
    }
    if (barChartEl) {
      barChartEl.addEventListener('wheel', handleBarWheel, { passive: false });
    }
    if (allocationBarEl) {
      allocationBarEl.addEventListener('wheel', handlePieWheel, { passive: false });
    }
    
    return () => {
      if (pieChartEl) {
        pieChartEl.removeEventListener('wheel', handlePieWheel);
      }
      if (barChartEl) {
        barChartEl.removeEventListener('wheel', handleBarWheel);
      }
      if (allocationBarEl) {
        allocationBarEl.removeEventListener('wheel', handlePieWheel);
      }
    };
  }, [getDisplayHoldings]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCurrencyCompact = (amount: number) => {
    if (Math.abs(amount) >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)}Cr`;
    } else if (Math.abs(amount) >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    } else if (Math.abs(amount) >= 1000) {
      return `₹${(amount / 1000).toFixed(1)}K`;
    }
    return formatCurrency(amount);
  };

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Get all portfolios
      const portfolios = await portfolioService.getPortfolios();
      
      if (portfolios.length === 0) {
        setPortfoliosData([]);
        setAggregatedHoldings([]);
        return;
      }

      // Load holdings and current values for each portfolio
      const portfolioDataPromises = portfolios.map(async (portfolio): Promise<PortfolioData | null> => {
        try {
          const [holdingsResponse, currentValueResponse] = await Promise.all([
            portfolioService.getHoldings(portfolio.id),
            portfolioService.getCurrentValue(portfolio.id).catch(() => null),
          ]);

          const currentValue = currentValueResponse?.portfolio_value || holdingsResponse.total_invested;
          const profitLoss = currentValue - holdingsResponse.total_invested;
          const profitLossPercentage = holdingsResponse.total_invested > 0 
            ? (profitLoss / holdingsResponse.total_invested) * 100 
            : 0;

          // Merge holdings with current prices
          const holdingsWithPrices: HoldingWithPrices[] = holdingsResponse.holdings.map(holding => {
            const priceData = currentValueResponse?.holdings_with_prices?.find(
              (h: any) => h.symbol === holding.symbol
            );
            return {
              ...holding,
              current_price: priceData?.current_price || 0,
              current_value: priceData?.current_value || holding.invested_amount,
              profit_loss: priceData?.profit_loss || 0,
              profit_loss_percentage: priceData?.profit_loss_percentage || 0,
            };
          });

          return {
            portfolio,
            holdings: holdingsWithPrices,
            totalHoldings: holdingsResponse.total_holdings,
            totalInvested: holdingsResponse.total_invested,
            currentValue,
            profitLoss,
            profitLossPercentage,
          };
        } catch (err) {
          console.error(`Failed to load data for portfolio ${portfolio.name}:`, err);
          return null;
        }
      });

      const results = await Promise.all(portfolioDataPromises);
      const validResults = results.filter((r): r is PortfolioData => r !== null);
      setPortfoliosData(validResults);

      // Aggregate holdings across all portfolios
      const holdingsMap = new Map<string, AggregatedHolding>();
      
      validResults.forEach(pd => {
        pd.holdings.forEach((holding) => {
          const existing = holdingsMap.get(holding.symbol);
          const currentValue = holding.current_value || holding.invested_amount;
          const profitLoss = currentValue - holding.invested_amount;
          
          if (existing) {
            existing.totalQuantity += holding.quantity;
            existing.totalInvested += holding.invested_amount;
            existing.currentValue += currentValue;
            existing.profitLoss += profitLoss;
            existing.portfolioCount += 1;
          } else {
            holdingsMap.set(holding.symbol, {
              symbol: holding.symbol,
              name: holding.name || holding.symbol,
              totalQuantity: holding.quantity,
              totalInvested: holding.invested_amount,
              currentValue: currentValue,
              profitLoss: profitLoss,
              profitLossPercentage: 0,
              portfolioCount: 1,
            });
          }
        });
      });

      // Calculate percentages and sort
      const aggregated = Array.from(holdingsMap.values()).map(h => ({
        ...h,
        profitLossPercentage: h.totalInvested > 0 ? (h.profitLoss / h.totalInvested) * 100 : 0,
      }));
      
      aggregated.sort((a, b) => b.totalInvested - a.totalInvested);
      setAggregatedHoldings(aggregated);

    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Calculate totals
  const totals = portfoliosData.reduce(
    (acc, pd) => ({
      invested: acc.invested + pd.totalInvested,
      currentValue: acc.currentValue + pd.currentValue,
      holdings: acc.holdings + pd.totalHoldings,
      portfolios: acc.portfolios + 1,
    }),
    { invested: 0, currentValue: 0, holdings: 0, portfolios: 0 }
  );

  const totalProfitLoss = totals.currentValue - totals.invested;
  const totalProfitLossPercentage = totals.invested > 0 ? (totalProfitLoss / totals.invested) * 100 : 0;

  // Data for charts
  const portfolioAllocationData = portfoliosData.map((pd, index) => ({
    name: pd.portfolio.name,
    value: pd.totalInvested,
    color: COLORS[index % COLORS.length],
  }));

  const topHoldingsData = aggregatedHoldings.slice(0, 10).map(h => ({
    symbol: h.symbol.replace('.NS', ''),
    invested: h.totalInvested,
    current: h.currentValue,
    pnl: h.profitLoss,
  }));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 1, py: 0 }}>
        {error}
        <Button onClick={loadDashboardData} sx={{ ml: 2 }}>Retry</Button>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box>
          <Typography variant="h6">
            Welcome back, {user?.first_name || 'Investor'}!
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Portfolio overview across {totals.portfolios} portfolio{totals.portfolios !== 1 ? 's' : ''} • {totals.holdings} holdings
          </Typography>
        </Box>
        <Tooltip title="Refresh data">
          <IconButton onClick={loadDashboardData} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {portfoliosData.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No portfolios found
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Create a portfolio to start tracking your investments
          </Typography>
          <Button variant="contained" onClick={() => navigate('/portfolio')}>
            Go to Portfolio
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {/* Summary Stats Row */}
          <Grid item xs={6} sm={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" variant="caption">Total Invested</Typography>
                    <Typography variant="h6">{formatCurrencyCompact(totals.invested)}</Typography>
                  </Box>
                  <AttachMoney color="primary" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" variant="caption">Current Value</Typography>
                    <Typography variant="h6">{formatCurrencyCompact(totals.currentValue)}</Typography>
                  </Box>
                  <AccountBalance color="info" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ 
              height: '100%',
              bgcolor: totalProfitLoss >= 0 ? 'success.light' : 'error.light',
              color: totalProfitLoss >= 0 ? 'success.contrastText' : 'error.contrastText'
            }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Profit/Loss</Typography>
                    <Typography variant="h6">
                      {totalProfitLoss >= 0 ? '+' : ''}{formatCurrencyCompact(totalProfitLoss)}
                    </Typography>
                  </Box>
                  {totalProfitLoss >= 0 ? <TrendingUp /> : <TrendingDown />}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ 
              height: '100%',
              bgcolor: totalProfitLossPercentage >= 0 ? 'success.light' : 'error.light',
              color: totalProfitLossPercentage >= 0 ? 'success.contrastText' : 'error.contrastText'
            }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Return</Typography>
                    <Typography variant="h6">
                      {totalProfitLossPercentage >= 0 ? '+' : ''}{totalProfitLossPercentage.toFixed(2)}%
                    </Typography>
                  </Box>
                  {totalProfitLossPercentage >= 0 ? <TrendingUp /> : <TrendingDown />}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Portfolio Cards */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ mt: 1 }}>
              Portfolio Breakdown
            </Typography>
            <Grid container spacing={1.5}>
              {portfoliosData.map((pd, index) => (
                <Grid item xs={12} sm={6} md={4} key={pd.portfolio.id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 3,
                      }
                    }}
                    onClick={() => navigate('/portfolio')}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box 
                            sx={{ 
                              width: 12, 
                              height: 12, 
                              borderRadius: '50%', 
                              bgcolor: COLORS[index % COLORS.length] 
                            }} 
                          />
                          <Typography variant="subtitle2" noWrap sx={{ maxWidth: 150 }}>
                            {pd.portfolio.name}
                          </Typography>
                        </Box>
                        <Chip 
                          label={`${pd.totalHoldings} stocks`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                      
                      <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
                        <Typography variant="body2" color="textSecondary">Invested</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrencyCompact(pd.totalInvested)}
                        </Typography>
                      </Box>
                      
                      <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
                        <Typography variant="body2" color="textSecondary">Current</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrencyCompact(pd.currentValue)}
                        </Typography>
                      </Box>

                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="textSecondary">P&L</Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography 
                            variant="body2" 
                            fontWeight="bold"
                            color={pd.profitLoss >= 0 ? 'success.main' : 'error.main'}
                          >
                            {pd.profitLoss >= 0 ? '+' : ''}{formatCurrencyCompact(pd.profitLoss)}
                          </Typography>
                          <Chip
                            label={`${pd.profitLossPercentage >= 0 ? '+' : ''}${pd.profitLossPercentage.toFixed(1)}%`}
                            size="small"
                            color={pd.profitLossPercentage >= 0 ? 'success' : 'error'}
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        </Box>
                      </Box>

                      {/* Progress bar showing portfolio proportion */}
                      <LinearProgress 
                        variant="determinate" 
                        value={(pd.totalInvested / totals.invested) * 100}
                        sx={{ 
                          mt: 1, 
                          height: 4, 
                          borderRadius: 2,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: COLORS[index % COLORS.length],
                          }
                        }}
                      />
                      <Typography variant="caption" color="textSecondary">
                        {((pd.totalInvested / totals.invested) * 100).toFixed(1)}% of total
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Charts Row */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2, height: 300, position: 'relative' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PieChartIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2">Portfolio Allocation</Typography>
                </Box>
                <Tooltip title="View detailed allocation">
                  <IconButton size="small" onClick={() => { setPieChartZoom(15); setSelectedPortfolioId(null); setAllocationModalOpen(true); }}>
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              {portfolioAllocationData.length > 0 ? (
                <ResponsiveContainer width="100%" height="85%">
                  <PieChart>
                    <Pie
                      data={portfolioAllocationData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {portfolioAllocationData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value: number) => formatCurrency(value)}
                      labelFormatter={(label) => `Portfolio: ${label}`}
                      contentStyle={{ fontSize: '0.75rem', padding: '4px 8px' }}
                      labelStyle={{ fontSize: '0.7rem', marginBottom: 2 }}
                    />
                    <Legend 
                      verticalAlign="middle" 
                      align="right" 
                      layout="vertical"
                      formatter={(value) => <span style={{ fontSize: '0.75rem' }}>{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="85%">
                  <Typography color="textSecondary">No data</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 2, height: 300, position: 'relative' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <ChartIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2">Top 10 Holdings by Investment</Typography>
                </Box>
                <Tooltip title="View all holdings">
                  <IconButton size="small" onClick={() => { setBarChartZoom(50); setHoldingsModalOpen(true); }}>
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              {topHoldingsData.length > 0 ? (
                <ResponsiveContainer width="100%" height="85%">
                  <BarChart data={topHoldingsData} layout="vertical" margin={{ left: 60, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis type="number" tickFormatter={(v) => formatCurrencyCompact(v)} fontSize={10} />
                    <YAxis type="category" dataKey="symbol" width={55} fontSize={10} />
                    <RechartsTooltip 
                      formatter={(value: number, name: string) => [formatCurrency(value), name]}
                      contentStyle={{ fontSize: '0.75rem', padding: '4px 8px' }}
                      labelStyle={{ fontSize: '0.7rem', marginBottom: 2 }}
                    />
                    <Bar dataKey="invested" fill="#2196F3" name="Invested" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="current" fill="#4CAF50" name="Current Value" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="85%">
                  <Typography color="textSecondary">No holdings data</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Holdings Table */}
          <Grid item xs={12}>
            <Paper sx={{ overflow: 'hidden' }}>
              <Box px={2} py={1.5} display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2">All Holdings ({aggregatedHoldings.length})</Typography>
                <Button 
                  size="small" 
                  onClick={() => navigate('/portfolio')}
                  endIcon={<ViewIcon fontSize="small" />}
                >
                  View Details
                </Button>
              </Box>
              <TableContainer sx={{ maxHeight: 350 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><Typography variant="caption" fontWeight="bold">Stock</Typography></TableCell>
                      <TableCell align="right"><Typography variant="caption" fontWeight="bold">Qty</Typography></TableCell>
                      <TableCell align="right"><Typography variant="caption" fontWeight="bold">Invested</Typography></TableCell>
                      <TableCell align="right"><Typography variant="caption" fontWeight="bold">Current</Typography></TableCell>
                      <TableCell align="right"><Typography variant="caption" fontWeight="bold">P&L</Typography></TableCell>
                      <TableCell align="right"><Typography variant="caption" fontWeight="bold">Return</Typography></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aggregatedHoldings.slice(0, 20).map((holding) => (
                      <TableRow 
                        key={holding.symbol}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/analysis?symbol=${encodeURIComponent(holding.symbol)}`)}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="body2" fontWeight="medium">
                              {holding.symbol.replace('.NS', '')}
                            </Typography>
                            {holding.portfolioCount > 1 && (
                              <Chip 
                                label={`${holding.portfolioCount}p`} 
                                size="small" 
                                sx={{ height: 16, fontSize: '0.6rem' }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{holding.totalQuantity.toLocaleString()}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{formatCurrencyCompact(holding.totalInvested)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{formatCurrencyCompact(holding.currentValue)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            color={holding.profitLoss >= 0 ? 'success.main' : 'error.main'}
                            fontWeight="medium"
                          >
                            {holding.profitLoss >= 0 ? '+' : ''}{formatCurrencyCompact(holding.profitLoss)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${holding.profitLossPercentage >= 0 ? '+' : ''}${holding.profitLossPercentage.toFixed(1)}%`}
                            size="small"
                            color={holding.profitLossPercentage >= 0 ? 'success' : 'error'}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {aggregatedHoldings.length > 20 && (
                <Box p={1} textAlign="center" bgcolor="grey.50">
                  <Typography variant="caption" color="textSecondary">
                    Showing top 20 of {aggregatedHoldings.length} holdings
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Portfolio Allocation Detail Modal */}
      <Dialog 
        open={allocationModalOpen} 
        onClose={() => { setAllocationModalOpen(false); setSelectedPortfolioId(null); }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <PieChartIcon color="primary" />
              <Typography variant="h6">Portfolio Allocation Details</Typography>
              {selectedPortfolioId !== null && (
                <Chip 
                  label={portfoliosData.find(pd => pd.portfolio.id === selectedPortfolioId)?.portfolio.name || 'Selected'}
                  color="primary"
                  size="small"
                  onDelete={() => { setSelectedPortfolioId(null); setPieChartZoom(15); }}
                />
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={showPercentage} 
                    onChange={(e) => setShowPercentage(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="caption">Show %</Typography>}
              />
              <IconButton onClick={() => { setAllocationModalOpen(false); setSelectedPortfolioId(null); }} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1.5}>
            {/* Portfolio Pie Chart - Clickable */}
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2">By Portfolio</Typography>
                <Typography variant="caption" color="textSecondary">
                  👆 Click to drill down
                </Typography>
              </Box>
              <Box height={350} sx={{ cursor: 'pointer' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={portfolioAllocationData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                      onClick={handlePortfolioPieClick}
                      style={{ cursor: 'pointer' }}
                    >
                      {portfolioAllocationData.map((entry, index) => {
                        const portfolio = portfoliosData.find(pd => pd.portfolio.name === entry.name);
                        const isSelected = portfolio && portfolio.portfolio.id === selectedPortfolioId;
                        return (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.color} 
                            stroke={isSelected ? '#000' : undefined}
                            strokeWidth={isSelected ? 3 : 0}
                            style={{ cursor: 'pointer' }}
                          />
                        );
                      })}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value: number) => formatCurrency(value)}
                      contentStyle={{ fontSize: '0.75rem', padding: '4px 8px' }}
                      labelStyle={{ fontSize: '0.7rem', marginBottom: 2 }}
                    />
                    <Legend wrapperStyle={{ fontSize: '0.7rem' }} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              {selectedPortfolioId !== null && (
                <Box textAlign="center" mt={1}>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => { setSelectedPortfolioId(null); setPieChartZoom(15); }}
                  >
                    Show All Portfolios
                  </Button>
                </Box>
              )}
            </Grid>
            
            {/* Stock-wise Allocation Pie Chart - Zoomable */}
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2">
                  Top {Math.min(pieChartZoom, getDisplayHoldings().length)} Stocks
                  {selectedPortfolioId !== null && ` (${portfoliosData.find(pd => pd.portfolio.id === selectedPortfolioId)?.portfolio.name})`}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  🖱️ Scroll to zoom
                </Typography>
              </Box>
              <Box 
                ref={pieChartRef}
                height={350} 
                sx={{ cursor: 'ns-resize' }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={(() => {
                        const displayHoldings = getDisplayHoldings();
                        const topItems = displayHoldings.slice(0, pieChartZoom);
                        const othersTotal = displayHoldings.slice(pieChartZoom).reduce((sum, h) => sum + h.totalInvested, 0);
                        const data = topItems.map((h, i) => ({
                          name: h.symbol.replace('.NS', ''),
                          value: h.totalInvested,
                          color: COLORS[i % COLORS.length]
                        }));
                        if (othersTotal > 0) {
                          data.push({ name: `Others (${displayHoldings.length - pieChartZoom})`, value: othersTotal, color: '#9E9E9E' });
                        }
                        return data;
                      })()}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={1}
                      dataKey="value"
                      label={({ name, percent }) => percent > 0.03 ? `${name}` : ''}
                    >
                      {(() => {
                        const displayHoldings = getDisplayHoldings();
                        const count = Math.min(pieChartZoom, displayHoldings.length);
                        const hasOthers = displayHoldings.length > pieChartZoom;
                        const cells = [];
                        for (let i = 0; i < count; i++) {
                          cells.push(<Cell key={`stock-cell-${i}`} fill={COLORS[i % COLORS.length]} />);
                        }
                        if (hasOthers) {
                          cells.push(<Cell key="others-cell" fill="#9E9E9E" />);
                        }
                        return cells;
                      })()}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value: number, name: string) => [formatCurrency(value), name]}
                      contentStyle={{ fontSize: '0.75rem', padding: '4px 8px' }}
                      labelStyle={{ fontSize: '0.7rem', marginBottom: 2 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Grid>

            {/* Stock Allocation Bar Chart - Zoomable */}
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2">
                  Top {Math.min(pieChartZoom, getDisplayHoldings().length)} Holdings
                  {selectedPortfolioId !== null && ` (${portfoliosData.find(pd => pd.portfolio.id === selectedPortfolioId)?.portfolio.name})`}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  🖱️ Scroll to zoom
                </Typography>
              </Box>
              <Box 
                ref={allocationBarChartRef}
                height={350} 
                sx={{ cursor: 'ns-resize' }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={(() => {
                      const displayHoldings = getDisplayHoldings();
                      const totalInvested = displayHoldings.reduce((sum, h) => sum + h.totalInvested, 0);
                      return displayHoldings.slice(0, pieChartZoom).map(h => ({
                        symbol: h.symbol.replace('.NS', ''),
                        value: showPercentage ? (h.totalInvested / totalInvested) * 100 : h.totalInvested,
                        absoluteValue: h.totalInvested,
                        percentage: (h.totalInvested / totalInvested) * 100,
                      }));
                    })()} 
                    layout="vertical" 
                    margin={{ left: 60, right: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis 
                      type="number" 
                      tickFormatter={(v) => showPercentage ? `${v.toFixed(1)}%` : formatCurrencyCompact(v)} 
                      fontSize={9}
                      domain={showPercentage ? [0, 'auto'] : undefined}
                    />
                    <YAxis type="category" dataKey="symbol" width={55} fontSize={9} />
                    <RechartsTooltip 
                      formatter={(value: number, name: string, props: any) => {
                        const { payload } = props;
                        return [
                          `${formatCurrency(payload.absoluteValue)} (${payload.percentage.toFixed(1)}%)`,
                          'Invested'
                        ];
                      }}
                      contentStyle={{ fontSize: '0.75rem', padding: '4px 8px' }}
                      labelStyle={{ fontSize: '0.7rem', marginBottom: 2 }}
                    />
                    <Bar dataKey="value" fill="#2196F3" name="Invested" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
            
            {/* Portfolio Details Table */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Portfolio Breakdown</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Portfolio</TableCell>
                      <TableCell align="right">Holdings</TableCell>
                      <TableCell align="right">Invested</TableCell>
                      <TableCell align="right">Current</TableCell>
                      <TableCell align="right">P&L</TableCell>
                      <TableCell align="right">Return %</TableCell>
                      <TableCell align="right">Allocation %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {portfoliosData.map((pd, index) => (
                      <TableRow key={pd.portfolio.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box 
                              sx={{ 
                                width: 12, 
                                height: 12, 
                                borderRadius: '50%', 
                                bgcolor: COLORS[index % COLORS.length] 
                              }} 
                            />
                            {pd.portfolio.name}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{pd.totalHoldings}</TableCell>
                        <TableCell align="right">{formatCurrency(pd.totalInvested)}</TableCell>
                        <TableCell align="right">{formatCurrency(pd.currentValue)}</TableCell>
                        <TableCell align="right">
                          <Typography 
                            color={pd.profitLoss >= 0 ? 'success.main' : 'error.main'}
                          >
                            {pd.profitLoss >= 0 ? '+' : ''}{formatCurrency(pd.profitLoss)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${pd.profitLossPercentage >= 0 ? '+' : ''}${pd.profitLossPercentage.toFixed(2)}%`}
                            size="small"
                            color={pd.profitLossPercentage >= 0 ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {((pd.totalInvested / totals.invested) * 100).toFixed(1)}%
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Total Row */}
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Total</strong></TableCell>
                      <TableCell align="right"><strong>{totals.holdings}</strong></TableCell>
                      <TableCell align="right"><strong>{formatCurrency(totals.invested)}</strong></TableCell>
                      <TableCell align="right"><strong>{formatCurrency(totals.currentValue)}</strong></TableCell>
                      <TableCell align="right">
                        <Typography 
                          fontWeight="bold"
                          color={totalProfitLoss >= 0 ? 'success.main' : 'error.main'}
                        >
                          {totalProfitLoss >= 0 ? '+' : ''}{formatCurrency(totalProfitLoss)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={`${totalProfitLossPercentage >= 0 ? '+' : ''}${totalProfitLossPercentage.toFixed(2)}%`}
                          size="small"
                          color={totalProfitLossPercentage >= 0 ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="right"><strong>100%</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAllocationModalOpen(false)}>Close</Button>
          <Button variant="contained" onClick={() => { setAllocationModalOpen(false); navigate('/portfolio'); }}>
            Go to Portfolio
          </Button>
        </DialogActions>
      </Dialog>

      {/* Holdings Detail Modal */}
      <Dialog 
        open={holdingsModalOpen} 
        onClose={() => setHoldingsModalOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <ChartIcon color="primary" />
              <Typography variant="h6">All Holdings ({aggregatedHoldings.length} stocks)</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={showPercentage} 
                    onChange={(e) => setShowPercentage(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="caption">Show %</Typography>}
              />
              <IconButton onClick={() => setHoldingsModalOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1.5}>
            {/* Full Holdings Chart - Zoomable */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2">
                  Top {Math.min(barChartZoom, aggregatedHoldings.length)} Holdings - {showPercentage ? 'Allocation %' : 'Invested vs Current Value'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  🖱️ Scroll on chart to show more/fewer holdings
                </Typography>
              </Box>
              <Box 
                ref={barChartRef}
                height={Math.min(Math.min(barChartZoom, aggregatedHoldings.length) * 22 + 80, 600)}
                sx={{ cursor: 'ns-resize', overflow: 'hidden' }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={(() => {
                      const totalInvested = aggregatedHoldings.reduce((sum, h) => sum + h.totalInvested, 0);
                      const totalCurrent = aggregatedHoldings.reduce((sum, h) => sum + h.currentValue, 0);
                      return aggregatedHoldings.slice(0, barChartZoom).map(h => ({
                        symbol: h.symbol.replace('.NS', ''),
                        'Invested Amount': showPercentage ? (h.totalInvested / totalInvested) * 100 : h.totalInvested,
                        'Current Value': showPercentage ? (h.currentValue / totalCurrent) * 100 : h.currentValue,
                        absoluteInvested: h.totalInvested,
                        absoluteCurrent: h.currentValue,
                        investedPct: (h.totalInvested / totalInvested) * 100,
                        currentPct: (h.currentValue / totalCurrent) * 100,
                      }));
                    })()} 
                    layout="vertical" 
                    margin={{ left: 80, right: 30, top: 10, bottom: 10 }}
                    barGap={0}
                    barCategoryGap="15%"
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis 
                      type="number" 
                      tickFormatter={(v) => showPercentage ? `${v.toFixed(1)}%` : formatCurrencyCompact(v)} 
                      fontSize={10}
                      domain={showPercentage ? [0, 'auto'] : undefined}
                    />
                    <YAxis 
                      type="category" 
                      dataKey="symbol" 
                      width={70} 
                      fontSize={10}
                      tick={{ fill: '#333' }}
                    />
                    <RechartsTooltip 
                      formatter={(value: number, name: string, props: any) => {
                        const { payload } = props;
                        if (name === 'Invested Amount') {
                          return [`${formatCurrency(payload.absoluteInvested)} (${payload.investedPct.toFixed(1)}%)`, name];
                        } else {
                          return [`${formatCurrency(payload.absoluteCurrent)} (${payload.currentPct.toFixed(1)}%)`, name];
                        }
                      }}
                      labelFormatter={(label) => `Stock: ${label}`}
                      contentStyle={{ fontSize: '0.75rem', padding: '4px 8px' }}
                      labelStyle={{ fontSize: '0.7rem', marginBottom: 2 }}
                    />
                    <Legend wrapperStyle={{ paddingTop: 10, fontSize: '0.75rem' }} />
                    <Bar 
                      dataKey="Invested Amount" 
                      fill="#2196F3" 
                      radius={[0, 4, 4, 0]}
                    />
                    <Bar 
                      dataKey="Current Value" 
                      fill="#4CAF50" 
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Grid>

            {/* Full Holdings Table */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Complete Holdings List</Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Stock</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Invested</TableCell>
                      <TableCell align="right">Current Value</TableCell>
                      <TableCell align="right">P&L</TableCell>
                      <TableCell align="right">Return %</TableCell>
                      <TableCell align="center">Portfolios</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aggregatedHoldings.map((holding) => (
                      <TableRow 
                        key={holding.symbol}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => { setHoldingsModalOpen(false); navigate(`/analysis?symbol=${encodeURIComponent(holding.symbol)}`); }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {holding.symbol.replace('.NS', '')}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {holding.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{holding.totalQuantity.toLocaleString()}</TableCell>
                        <TableCell align="right">{formatCurrency(holding.totalInvested)}</TableCell>
                        <TableCell align="right">{formatCurrency(holding.currentValue)}</TableCell>
                        <TableCell align="right">
                          <Typography 
                            color={holding.profitLoss >= 0 ? 'success.main' : 'error.main'}
                            fontWeight="medium"
                          >
                            {holding.profitLoss >= 0 ? '+' : ''}{formatCurrency(holding.profitLoss)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${holding.profitLossPercentage >= 0 ? '+' : ''}${holding.profitLossPercentage.toFixed(2)}%`}
                            size="small"
                            color={holding.profitLossPercentage >= 0 ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {holding.portfolioCount > 1 ? (
                            <Chip label={`${holding.portfolioCount}`} size="small" />
                          ) : (
                            <Typography variant="body2">1</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHoldingsModalOpen(false)}>Close</Button>
          <Button variant="contained" onClick={() => { setHoldingsModalOpen(false); navigate('/portfolio'); }}>
            Go to Portfolio
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
